/*
 * <prism-quantity> — accessible quantity stepper.
 *
 * Wraps a number input with decrement/increment buttons, respecting the
 * input's min/max/step. Dispatches a native 'change' event after adjusting so
 * product forms and the cart drawer can react through their usual listeners.
 *
 * Markup contract:
 *   <prism-quantity>
 *     <button type="button" data-qty-minus aria-label="Decrease quantity">−</button>
 *     <input type="number" min="1" step="1" value="1">
 *     <button type="button" data-qty-plus aria-label="Increase quantity">+</button>
 *   </prism-quantity>
 */
if (!customElements.get('prism-quantity')) {
  customElements.define(
    'prism-quantity',
    class PrismQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.minus = this.querySelector('[data-qty-minus]');
        this.plus = this.querySelector('[data-qty-plus]');
        if (!this.input) return;

        this.onMinus = this.step.bind(this, -1);
        this.onPlus = this.step.bind(this, 1);
        if (this.minus) this.minus.addEventListener('click', this.onMinus);
        if (this.plus) this.plus.addEventListener('click', this.onPlus);
        this.sync();
        this.input.addEventListener('change', this.sync.bind(this));
      }

      disconnectedCallback() {
        if (this.minus) this.minus.removeEventListener('click', this.onMinus);
        if (this.plus) this.plus.removeEventListener('click', this.onPlus);
      }

      get step_() {
        return parseFloat(this.input.step) || 1;
      }
      get min() {
        return this.input.min === '' ? 1 : parseFloat(this.input.min);
      }
      get max() {
        return this.input.max === '' ? Infinity : parseFloat(this.input.max);
      }

      step(direction) {
        var current = parseFloat(this.input.value) || 0;
        var next = current + direction * this.step_;
        next = Math.min(this.max, Math.max(this.min, next));
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      }

      // Disable buttons at the bounds for clear affordance.
      sync() {
        var value = parseFloat(this.input.value) || 0;
        if (this.minus) this.minus.disabled = value <= this.min;
        if (this.plus) this.plus.disabled = value >= this.max;
      }
    }
  );
}
